import { Suspense, lazy, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppProviders } from "@/components/providers/app-providers";
import { ServiceWorkerRegistrar } from "@/components/providers/service-worker";
import { ErrorBoundary } from "@/components/error-boundary";
import { BrandLoader } from "@/components/ui/brand-loader";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { apiFetch } from "@/lib/api";
import { FALLBACK_THEME } from "@/lib/fallback-theme";
import type { ThemeDto } from "@/lib/types";
import { AppRoutes } from "@/routes";

const OfflinePage = lazy(() => import("@/pages/offline"));

/**
 * The admin-configured palette used to be fetched while server-rendering the
 * root layout. There is no server render any more, so it is fetched once on
 * boot; until it arrives the defaults compiled into globals.css are already on
 * screen, so there is no unbranded flash.
 */
function useBrand(): ThemeDto {
  const [brand, setBrand] = useState<ThemeDto>(FALLBACK_THEME);

  useEffect(() => {
    let cancelled = false;

    apiFetch<ThemeDto>("/api/theme", { suppressAuthRedirect: true })
      .then((theme) => {
        if (!cancelled) {
          setBrand(theme);
        }
      })
      .catch(() => {
        // Keep the fallback palette; the portal stays usable unbranded.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return brand;
}

export function App() {
  const brand = useBrand();
  const { pathname } = useLocation();
  const online = useOnlineStatus();

  return (
    <AppProviders brand={brand} initialMe={null}>
      <ErrorBoundary resetKey={pathname}>
        <Suspense fallback={<BrandLoader label="Loading" />}>
          {online ? <AppRoutes /> : <OfflinePage />}
        </Suspense>
      </ErrorBoundary>
      <ServiceWorkerRegistrar />
    </AppProviders>
  );
}
